<template>
  <AsidePanel
    ref="asidePanel"
    :class="$style.AuthNPoliciesAsidePanel"
    :components="components"
    :disableCta="shouldDisableCta"
    :handleCancel="redirectToAuthNPolicies"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="$options.itemType"
    :noRedirect="noRedirect"
    redirectTo="/#/security/authnpolicies"
    :saveText="saveText"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="locked"
      />
    </template>
    <template v-slot:status>
      <PolicyStatus />
    </template>
    <template
      v-if="isPlatformPlusFeature"
      v-slot:asideHeaderMessages
    >
      <PlatformPlusBadgeCard
        :features="platformPlusFeatureList"
        :linkText="platformPlusLinkText"
        :message="platformPlusMessage"
        :pluralFeatureName="platformPlusFeatureName"
        :title="platformPlusTitle"
      />
    </template>
  </AsidePanel>
</template>
<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import {
  ItemTypeConsts,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { authnPolicyType } from 'client/Security/AuthNPolicyType';
import {
  eventLocations,
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import AuthNPoliciesAsidePanelSectionsLegacy from 'client/Security/AuthNPoliciesAsidePanel/AuthNPoliciesAsidePanelSectionsLegacy.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import MspState from 'client/components/models/MspState';
import PlatformPlusBadgeCard from 'client/components/PlatformPlusBadge/PlatformPlusBadgeCard.vue';
import PlatformPlusFeatureConstants from 'client/util/Constants/PlatformPlusFeatureConstants';
import PolicyStatus from 'client/Security/AuthNPoliciesAsidePanel/PolicyStatus.vue';
import Util from 'client/util/Util';

const {
  authNPolicies,
} = ItemTypeConsts;
const authNPolicyModel = StoreModelMap[authNPolicies];
const authNPolicyModule = StoreModuleMap[authNPolicies];
const {
  description,
  iconName,
  location,
  section,
  text,
} = eventProperties;
const { conditionalPoliciesAside } = eventLocations;
const planModule = 'PlanModule';
const zeroTrustFeatureCode = trialFeatures.zeroTrust.featureCode;

const platformPlusFeatureList = [
  PlatformPlusFeatureConstants.deviceTrust,
  PlatformPlusFeatureConstants.networkTrust,
];

export default {
  name: 'AuthNPoliciesAsidePanelLegacy',

  itemType: ItemTypeConsts.authNPolicies,

  components: {
    AsidePanel,
    AsideIcon,
    PlatformPlusBadgeCard,
    PolicyStatus,
  },

  mixins: [
    AsidePanelPropsMixin,
    FeatureTrialsMixin,
  ],

  data() {
    return {
      components: [{
        label: 'Details',
        vueComponent: AuthNPoliciesAsidePanelSectionsLegacy,
      }],
      validationErrorMessage: 'Please review the Details Tab for missing or invalid information',
      platformPlusFeatureName: 'Conditions',
      platformPlusFeatureList,
      platformPlusTitle: 'Platform Plus Features Available',
      platformPlusMessage: 'Conditions such as Device and Network Trust are part of our Platform Plus plan.',
      platformPlusLinkText: 'Learn more.',
    };
  },

  computed: {
    ...mapGetters(authNPolicyModule, ['getSelectedItemsCount']),

    ...mapGetters(authNPolicyModel, [
      'canSaveItem',
      'item',
      'hasItemChanged',
      'haveAssociationsChanged',
    ]),

    ...mapGetters('SettingsModule', [
      'hasBilling',
    ]),

    hasBillingRole() {
      return this.hasBilling
        && !MspState.isAnMspOrChild;
    },

    isPlatformPlusFeature() {
      return get(this.item, 'type') !== authnPolicyType.ldap && !this.planHasFeature(zeroTrustFeatureCode);
    },

    saveText() {
      return this.itemId ? 'update policy' : 'create policy';
    },

    /**
     * @condition hasItemChanged - getter from AuthNPolicyModel.
     *              Checks to see if a policy has been edited.
     * @condition canSaveItem - getter from AuthNPolicyModel.
     *             Set in the ActionSection compnent based on which MFA is configured.
     * @condition item.name.length - check if the user has entered policy name
     * @todo hasItemChanged should be remove once 2022-10-04-ldap-totp feature flag is removed
     */
    shouldDisableCta() {
      return !this.hasItemChanged || !this.canSaveItem || !this.item.name.length;
    },
  },

  created() {
    this.fetchEntitledFeatureCodes();
    this.fetchPlanInfo();
  },

  methods: {
    ...mapActions(authNPolicyModel, [
      'saveItem',
    ]),

    ...mapActions(authNPolicyModule, [
      'refreshCollection',
    ]),

    ...mapActions('AlertModule', [
      'triggerErrorAlert',
    ]),

    ...mapActions(planModule, ['fetchPlan']),

    async fetchPlanInfo() {
      if (this.hasBillingRole) await this.fetchPlan();
    },

    async handleSave() {
      await this.$validator.validate();
      if (!this.$validator.errors.any()) {
        const item = await this.saveItem();

        this.refreshCollection({
          item,
          forceRefresh: this.getSelectedItemsCount > 0,
        });
        this.refreshTrialData(zeroTrustFeatureCode);
      } else {
        throw Error(this.validationErrorMessage);
      }
    },

    redirectToAuthNPolicies() {
      EventHelper.triggerIconClicked({
        [description]: 'Closes CAP Aside',
        [iconName]: 'close',
        [location]: conditionalPoliciesAside,
        [section]: 'upper right hand corner',
        [text]: 'X',
      });

      Util.redirect('/#/security/authnpolicies');
    },
  },
};
</script>

<style module>
.AuthNPoliciesAsidePanel {
  --headerTextTextOverflow: ellipsis;
  --headerTextWhiteSpace: nowrap;
}
</style>
